import React from 'react';
import { motion } from 'framer-motion';
import { AwardIcon, BookOpenIcon, CalendarIcon, CheckCircle2Icon, GraduationCapIcon, MessageCircleIcon, ShieldCheckIcon, TargetIcon, UsersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'rose' | 'sky';
type Track = {
  id: string;
  icon: typeof GraduationCapIcon;
  title: string;
  description: string;
  outcome: string;
  duration: string;
  price: string;
  audience: string[];
  included: Array<{
    icon: typeof BookOpenIcon;
    text: string;
  }>;
  outcomes: string[];
  curriculum: Array<{
    title: string;
    description: string;
  }>;
  priceFeatures: Array<{
    icon: typeof AwardIcon;
    text: string;
  }>;
  tone: Tone;
};
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]'
};
const tracks: Track[] = [{
  id: 'teacher-certification',
  icon: GraduationCapIcon,
  title: 'اعتماد المعلّمين',
  description: 'برنامج اعتماد منظم عبر الإنترنت للمربين الذين يدعمون الأطفال المسلمين في الدول غير المسلمة، قائم على نمو الطفل ومعايير التدريس الأخلاقي والحماية.',
  outcome: 'كن معلّمًا معتمدًا وواثقًا، مؤهلًا لتعليم الأطفال المسلمين بعناية وحماية وأساليب أخلاقية تراعي هويتهم.',
  duration: '12 أسبوعًا',
  price: '€590',
  audience: ['المعلمون والمربون', 'آباء وأمهات التعليم المنزلي', 'مرشدو الشباب', 'العاملون في المجتمع'],
  included: [{
    icon: BookOpenIcon,
    text: 'تقديم منظم عبر الإنترنت'
  }, {
    icon: CheckCircle2Icon,
    text: 'معايير تدريس أخلاقية'
  }, {
    icon: ShieldCheckIcon,
    text: 'الحماية والمسؤولية'
  }, {
    icon: AwardIcon,
    text: 'شهادة إتمام'
  }],
  outcomes: ['اعتماد معترف به ضمن شبكة شركائنا', 'ثقة في الحماية والممارسة الأخلاقية', 'أساليب تدريس تراعي الهوية', 'مهارات عملية للفصل الدراسي عبر الإنترنت'],
  curriculum: [{
    title: 'علم النفس التربوي ونمو الطفل',
    description: 'مراحل النمو المعرفي والعاطفي والاجتماعي لدى الأطفال المسلمين.'
  }, {
    title: 'أسس التربية والأخلاق',
    description: 'الفلسفة التعليمية الإسلامية والمنهج النبوي وأدب المعلّم.'
  }, {
    title: 'البيداغوجيا الحديثة وأساليب التدريس',
    description: 'التعلم النشط والاستقصاء وأدوات التفكير النقدي.'
  }, {
    title: 'التعليم الدامج وتنوع الاحتياجات',
    description: 'تحديد عوائق التعلم واستراتيجيات التفريد والتعديلات الداعمة.'
  }, {
    title: 'تخطيط المنهج وتصميم المقررات',
    description: 'مواءمة المعايير التعليمية مع القيم الأساسية ومسارات التعلم المنظمة.'
  }, {
    title: 'تخطيط الدروس وتطوير الموارد',
    description: 'إنشاء خطط دروس قابلة للتنفيذ وجاذبة واختيار مواد أخلاقية.'
  }, {
    title: 'إدارة الصف والانضباط الإيجابي',
    description: 'بناء ثقافة تعلم محترمة والتعامل مع السلوك وتحفيز الطلاب.'
  }, {
    title: 'الحماية وحقوق الطفل والواجب القانوني',
    description: 'لوائح سلامة الطفل وتحديد المخاطر والامتثال والسياسات المؤسسية.'
  }, {
    title: 'تكنولوجيا التعليم والثقافة الرقمية',
    description: 'استخدام الأدوات الرقمية الحديثة بأمان وفاعلية في التعلم.'
  }, {
    title: 'تقييم الطلاب ومتابعة التقدم',
    description: 'التقييم التكويني والختامي ومعايير التقدير والتغذية الراجعة البنّاءة.'
  }, {
    title: 'تعاون وتواصل المعلّم والأسرة',
    description: 'بناء الثقة مع الأسر وعقد الاجتماعات وإعداد تقارير التقدم.'
  }, {
    title: 'التطبيق المهني وملف الإنجاز',
    description: 'عرض تدريسي نهائي ومراجعة ملف الإنجاز ومنح الاعتماد.'
  }],
  priceFeatures: [{
    icon: AwardIcon,
    text: 'شهادة عند الإتمام'
  }, {
    icon: UsersIcon,
    text: 'مجموعات صغيرة ومخصصة'
  }, {
    icon: ShieldCheckIcon,
    text: 'منح محدودة عبر الشراكات المجتمعية'
  }],
  tone: 'sage'
}, {
  id: 'community-leadership',
  icon: UsersIcon,
  title: 'القيادة المجتمعية',
  description: 'اعتماد لقادة ومنسقي المجتمع يجمع بين القيادة الأخلاقية الإسلامية والتواصل العملي وحل النزاعات وتصميم المشاريع المجتمعية.',
  outcome: 'قد مجتمعك بثقة، مزودًا بأطر أخلاقية ومهارات تواصل وخطة مشروع حقيقية قابلة للتنفيذ.',
  duration: '10 أسابيع',
  price: '€490',
  audience: ['قادة ومنسقو المجتمع', 'الأئمة والمساعدون في الأدوار المجتمعية', 'منسقو مجموعات الشباب والنساء', 'المتطوعون والمرشدون النشطون'],
  included: [{
    icon: TargetIcon,
    text: 'أطر قيادة قائمة على الأخلاق الإسلامية'
  }, {
    icon: MessageCircleIcon,
    text: 'أدوات للتواصل وحل النزاعات'
  }, {
    icon: BookOpenIcon,
    text: 'خطة مشروع مجتمعي عملية'
  }, {
    icon: AwardIcon,
    text: 'شهادة إتمام'
  }],
  outcomes: ['حقيبة أدوات للقيادة الأخلاقية', 'مهارات تواصل وحل نزاعات أقوى', 'مشروع مجتمعي جاهز للتنفيذ', 'اعتماد معترف به ضمن شبكة شركائنا'],
  curriculum: [{
    title: 'القيادة النبوية ونموذج الخدمة',
    description: 'الأطر الأخلاقية والمساءلة ومفهوم الأمانة.'
  }, {
    title: 'التقييم الاستراتيجي لاحتياجات المجتمع',
    description: 'التحليل السكاني ورسم البنية المحلية وتحديد الفجوات.'
  }, {
    title: 'التواصل المتقدم والعلاقات العامة',
    description: 'الخطابة والإعلام وتواصل الأزمات والحوار بين الثقافات.'
  }, {
    title: 'تصميم المشاريع والتخطيط التشغيلي',
    description: 'تحويل احتياجات المجتمع إلى خطط منظمة قابلة للتنفيذ والقياس.'
  }, {
    title: 'الحماية والأخلاق وإدارة المتطوعين',
    description: 'الحدود وبروتوكولات السلامة وتنسيق فرق المجتمع بفاعلية.'
  }, {
    title: 'الإدارة المالية وجمع التمويل الأخلاقي',
    description: 'كتابة المنح والتمويل الجماعي والميزانيات الحلال وتوزيع الموارد بشفافية.'
  }, {
    title: 'حل النزاعات والوساطة المجتمعية',
    description: 'إدارة الاحتكاكات وموازنة أصحاب المصلحة وبناء التوافق.'
  }, {
    title: 'الامتثال القانوني والأنظمة الغربية',
    description: 'هياكل الحوكمة وقوانين المؤسسات غير الربحية والامتثال وإدارة المخاطر.'
  }, {
    title: 'التسويق الاستراتيجي وحشد المجتمع',
    description: 'بناء الوعي والوصول الرقمي والمشاركة العامة المستدامة.'
  }, {
    title: 'إطلاق المشروع والاستدامة طويلة المدى',
    description: 'تنفيذ مشروع التخرج وقياس الأثر والتوسع الاستراتيجي.'
  }],
  priceFeatures: [{
    icon: AwardIcon,
    text: 'شهادة عند الإتمام'
  }, {
    icon: TargetIcon,
    text: 'خطة مشروع مجتمعي حقيقية'
  }, {
    icon: UsersIcon,
    text: 'منح محدودة عبر الشراكات المجتمعية'
  }],
  tone: 'rose'
}];
const standards = [{
  icon: ShieldCheckIcon,
  title: 'الحماية أولًا',
  body: 'تلتزم جميع البرامج بمعايير حماية صارمة. الثقة والحماية أمران لا يقبلان التنازل.'
}, {
  icon: CheckCircle2Icon,
  title: 'الممارسة الأخلاقية',
  body: 'يعمل الممارسون بشفافية ومساءلة واحترام لاستقلالية الأسرة.'
}, {
  icon: AwardIcon,
  title: 'إتمام معتمد',
  body: 'تُمنح الشهادات عند الإتمام الناجح حيثما ينطبق ذلك، ومعترف بها ضمن شبكتنا.'
}];
export function ArabicProfessionalDevelopment() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-1571" data-mp="2e4z7kfjcvb2w">
      <section className="relative isolate overflow-hidden" data-id="element-1572" data-mp="2e3fqwntsey0l">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-1573" data-mp="2lo058k7h6aic" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-1574" data-mp="2qm1lyyhfhxqn" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-1575" data-mp="2vgxq2wzxwbk4">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-1576" data-mp="30cmj0hhgeu7g">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-1577" data-mp="22qp40u2a9709">
              <span className="text-sm font-semibold text-forest-700" data-id="element-1578" data-mp="27r3c2c3vwgya">اعتماد وقيادة للمربين وبناة المجتمع</span>
              <h1 className="mt-3 font-display text-4xl font-semibold leading-[1.45] text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-1579" data-mp="2gpou865g1tyj">التطوير المهني</h1>
              <div className="mt-6 max-w-xl space-y-3 text-base leading-loose text-ink-700 md:text-lg" data-id="element-1580" data-mp="3o2qner2guxzi"><p data-id="element-1581" data-mp="3s42aopxvb74j">مسارات مهنية صُممت للمربين والمرشدين وقادة المجتمع الذين يدعمون الأسر المسلمة في الدول غير المسلمة.</p><p data-id="element-1582" data-mp="i21dvhlac9ru">تدريب عملي ومعايير حماية وقيادة أخلاقية، مع منح شهادات عند الإتمام حيثما ينطبق ذلك.</p></div>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-1583" data-mp="d3q4f7ywk1a5"><a href="#programs" data-id="element-1584" data-mp="rr2tv7rtyr5t"><Button variant="primary" size="lg" data-id="element-1585" data-mp="rrksuc5vnjhf">استكشف البرامج</Button></a><Link to="/ar/contact" data-id="element-1586" data-mp="rsvzqkumd7cr"><Button variant="secondary" size="lg" data-id="element-1587" data-mp="n0mc5bgz8jdn">اطلب معلومات</Button></Link></div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-1588" data-mp="10r7wz5pzr3e6"><div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-1589" data-mp="1apnd6dnhcs8w"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/mZ6s4PwoMdkaamZH9rLkBU/Prof-Dev.jpg" alt="مهني مسلم يسجّل نقاشًا تعليميًا بجانب حاسوب محمول وميكروفون" data-id="element-1590" data-mp="1ankqa2hmjf8e" /></div></motion.div>
          </div>
        </Container>
      </section>

      <section id="programs" className="scroll-mt-24 bg-sage-50/60 py-16 md:py-24" data-id="element-1591" data-mp="37j8s4e8d5o3e"><Container data-id="element-1592" data-mp="1n0emcrm9wlhc"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-1593" data-mp="1mzwknfgudf21">مساران مهنيان</h2><p className="mt-5 max-w-4xl leading-loose text-ink-700" data-id="element-1594" data-mp="1ry7uhq74zpk9">اختر المسار الذي يناسب دورك وأثرك. يركز المساران على الممارسة الأخلاقية والحماية والتطبيق الواقعي.</p><div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-1595" data-mp="1d8inhecd4rc2">{tracks.map((track, index) => <TrackCard key={track.id} track={track} index={index} data-id="element-1596" data-mp="1i7li04upvf5s" data-mpi={index} />)}</div><div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-1597" data-mp="148lvp3uofccs"><span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold text-forest-700" data-id="element-1598" data-mp="18ctn58sir6ep">قريبًا</span><p className="flex-1 text-center text-sm font-semibold leading-loose text-ink-700" data-id="element-1599" data-mp="zac0it8ycbr6">سنعلن قريبًا عن أسماء معلمينا ومربينا وأعضاء الهيئة الشرعية المعتمدين.</p></div></Container></section>

      {tracks.map((track, index) => <TrackDetail key={track.id} track={track} index={index} data-id="element-1600" data-mp="2tov30vthuf97" data-mpi={index} />)}

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-1601" data-mp="2097kp9p1ubup"><PatternDecor side="left" y="middle" size="md" data-id="element-1602" data-mp="20735hvlp9qf4" /><Container data-id="element-1603" data-mp="25062yetllk0l"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-1604" data-mp="250xyjpn73hqv">المعايير والاعتماد</h2><p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-1605" data-mp="1r9ssa4pbnr7k">تُبنى برامجنا المهنية على ثلاثة التزامات: الحماية والممارسة الأخلاقية والإتمام المعترف به.</p><div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-1606" data-mp="2jx6t4dw556wy">{standards.map((standard, index) => <StandardCard key={standard.title} standard={standard} index={index} data-id="element-1607" data-mp="2jvca554fjovi" data-mpi={index} />)}</div></Container></section>

      <section className="bg-[#EDF2F7]/60 py-16 md:py-24" data-id="element-1608" data-mp="29yziecsg3l3e"><Container data-id="element-1609" data-mp="29y7mf0uxrlcl"><h2 className="font-display text-2xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-1610" data-mp="29yhb8l48vmgp">القبول والمنح الدراسية</h2><p className="mt-5 max-w-4xl leading-loose text-ink-700" data-id="element-1611" data-mp="2f06o86b12a7f">قد تتضمن بعض المجموعات عددًا محدودًا من المقاعد المدعومة بالمنح عبر شراكات مجتمعية. وقد تشمل مسارات الاعتماد متطلبات للإتمام والتقييم.</p></Container></section>

      <section className="relative isolate overflow-hidden bg-sage-50/60 py-16 md:py-24" data-id="element-1612" data-mp="vze8wcxk3ug4"><PatternDecor side="right" y="bottom" size="md" data-id="element-1613" data-mp="w18rvlp9pchk" /><Container data-id="element-1614" data-mp="qz9oibtj2fkm"><div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-1615" data-mp="qzje3yanukps"><div className="max-w-xl" data-id="element-1616" data-mp="150ce2yjqe80a"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-1617" data-mp="14zkfrfyr1w6b">طوّر أثرك</h2><p className="mt-5 leading-loose text-ink-700" data-id="element-1618" data-mp="101iwov1bpwuu">اطلب التفاصيل واختر المسار المهني المناسب لدورك.</p></div><div className="flex flex-wrap gap-3" data-id="element-1619" data-mp="cbom6k4h8kdr"><Link to="/ar/waitlist" data-id="element-1620" data-mp="89tjbb6wdyxv"><Button variant="primary" data-id="element-1621" data-mp="8amklwn37w8m">انضم</Button></Link><Link to="/ar/contact" data-id="element-1622" data-mp="8bo50rpztv30"><Button variant="secondary" data-id="element-1623" data-mp="m184nooa2c83">اطلب معلومات</Button></Link></div></div></Container></section>
    </div>;
}
function TrackCard({
  track,
  index
}: {
  track: Track;
  index: number;
}) {
  const Icon = track.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} whileHover={{
    y: -2
  }} data-id="element-1624" data-mp="3pq1n2uxr6zl2"><a href={`#${track.id}`} className={`block h-full rounded-2xl border p-7 shadow-soft transition-shadow hover:shadow-card ${toneClasses[track.tone]}`} data-id="element-1625" data-mp="1oodahh3vjbjq"><span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-1626" data-mp="1tllxpw3vq5ru"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-1627" data-mp="1yeeyk8w88tyn" /></span><h3 className="mt-5 font-display text-xl font-semibold leading-loose text-ink-900" data-id="element-1628" data-mp="23d9i5d20qmjt">{track.title}</h3><p className="mt-3 leading-loose text-ink-700" data-id="element-1629" data-mp="15q12vzsm29q9">{track.description}</p><ul className="mt-5 space-y-2.5" data-id="element-1630" data-mp="1aoayucm5tecx">{track.included.map(item => <li key={item.text} className="flex items-start gap-2.5 text-sm leading-loose text-ink-700" data-id="element-1631" data-mp="1aouf7ow5vuou"><CheckCircle2Icon size={14} className="mt-1 shrink-0 text-forest-700" strokeWidth={1.8} aria-hidden="true" data-id="element-1632" data-mp="1fmm9uqo6xuwq" />{item.text}</li>)}</ul></a></motion.div>;
}
function TrackDetail({
  track,
  index
}: {
  track: Track;
  index: number;
}) {
  const Icon = track.icon;
  const backgrounds = ['bg-white/60', 'bg-sand-50/60'];
  return <section id={track.id} className={`scroll-mt-24 py-16 md:py-24 ${backgrounds[index]}`} data-id="element-1633" data-mp="2v2cnyx9k04yk"><Container data-id="element-1634" data-mp="jgarom3k5j3s"><article className="overflow-hidden rounded-3xl border border-sage-200 bg-white shadow-soft" data-id="element-1635" data-mp="jg0zqsyyd1vg"><div className="flex flex-col gap-6 border-b border-sage-200 bg-sage-50 px-7 py-8 md:flex-row md:items-center md:justify-between md:px-10" data-id="element-1636" data-mp="ffqujiizifhn"><div data-id="element-1637" data-mp="5pfdqve7cbtc"><div className="mb-3 flex flex-wrap items-center gap-3" data-id="element-1638" data-mp="5pfit9t27207"><span className="inline-flex rounded-full bg-forest-100 px-3 py-1 text-xs font-semibold text-forest-800" data-id="element-1639" data-mp="5ow7ibxwzbv5">{track.duration}</span><span className="text-sm font-medium text-ink-500" data-id="element-1640" data-mp="nygrayu99i1">اعتماد</span></div><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-1641" data-mp="3rv2lu0bfalnt">{track.title}</h2></div><div className="flex flex-col gap-3 md:items-end" data-id="element-1642" data-mp="3nvaic20vz5pb"><p className="font-display text-2xl text-forest-700" data-id="element-1643" data-mp="3iwh9imwjo2vh">{track.price} <span className="font-sans text-base text-ink-500" data-id="element-1644" data-mp="3iyafdzzc2j2f">/ اعتماد</span></p><Link to="/ar/waitlist" data-id="element-1645" data-mp="1m35naho3bsad"><Button variant="primary" data-id="element-1646" data-mp="1m1baxrqvx8hw">احجز مقعدك</Button></Link></div></div><div className="grid lg:grid-cols-12" data-id="element-1647" data-mp="1h5vxv20pbgmj"><div className="border-b border-sage-200 px-7 py-10 lg:col-span-7 lg:border-b-0 lg:border-l md:px-10" data-id="element-1648" data-mp="1h2qabr7q0msx"><h3 className="font-display text-xl font-semibold leading-loose text-ink-900 md:text-2xl" data-id="element-1649" data-mp="drucjouqros7">{track.outcome}</h3><p className="mt-4 leading-loose text-ink-700" data-id="element-1650" data-mp="4sz383wehyko">{track.description}</p><InfoGroup title="لمن صُمم هذا المسار؟" data-id="element-1651" data-mp="4speskr07zh3"><div className="flex flex-wrap gap-2" data-id="element-1652" data-mp="9rtmt826zr5u">{track.audience.map(item => <span key={item} className="rounded-lg border border-sand-200 bg-sand-50 px-3 py-1.5 text-sm text-ink-700" data-id="element-1653" data-mp="9u6dqapxhbv1">{item}</span>)}</div></InfoGroup><InfoGroup title="يشمل المسار" data-id="element-1654" data-mp="3vztj37vrl7lt"><div className="grid gap-4 sm:grid-cols-2" data-id="element-1655" data-mp="3w01x533miiyl">{track.included.map(item => {
                  const IncludedIcon = item.icon;
                  return <div key={item.text} className="flex items-center gap-3 text-sm leading-loose text-ink-700" data-id="element-1656" data-mp="3i9gbx7qp5wxj"><span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sage-100 text-forest-700" data-id="element-1657" data-mp="3ma9vxq10qrl5"><IncludedIcon size={14} strokeWidth={2} aria-hidden="true" data-id="element-1658" data-mp="38ah9w80fszji" /></span>{item.text}</div>;
                })}</div></InfoGroup><div className="mt-10 rounded-2xl border border-sage-200 bg-sage-50 p-6" data-id="element-1659" data-mp="3dav8kzg5ex4v"><h4 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-1660" data-mp="2tns3rnbojo45">ما الذي ستحققه في {track.duration}</h4><ul className="mt-4 space-y-3" data-id="element-1661" data-mp="2opqkb1acdms5">{track.outcomes.map(item => <li key={item} className="flex items-start gap-3 font-medium leading-loose text-ink-800" data-id="element-1662" data-mp="33lg9f8atqg13"><CheckCircle2Icon size={18} className="mt-1 shrink-0 text-forest-700" strokeWidth={2} aria-hidden="true" data-id="element-1663" data-mp="2ylk5ta61gqm2" />{item}</li>)}</ul></div></div><div className="bg-[#FAFAFA] px-7 py-10 lg:col-span-5 md:px-10" data-id="element-1664" data-mp="3ckjqy7ucendh"><h3 className="text-sm font-semibold text-forest-700" data-id="element-1665" data-mp="37nb968d3wlcr">منهج البرنامج</h3><ol className="mt-7 space-y-6 border-r border-sage-200 pr-6" data-id="element-1666" data-mp="37kxei0mc9b5q">{track.curriculum.map((subject, subjectIndex) => <li key={subject.title} className="relative" data-id="element-1667" data-mp="3hcl0hm04lzzt" data-mpi={subjectIndex}><span className="absolute -right-[37px] top-0 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-forest-700 bg-white text-[10px] font-bold text-forest-700" data-id="element-1668" data-mp="3hdw51o1ebbrz">{subjectIndex + 1}</span><h4 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-1669" data-mp="3rc231uk2zxrk">{subject.title}</h4><p className="mt-1 text-sm leading-loose text-ink-600" data-id="element-1670" data-mp="29vzhidlb94m">{subject.description}</p></li>)}</ol><div className="mt-10 rounded-2xl border border-sage-200 bg-white p-6 shadow-sm" data-id="element-1671" data-mp="78pfbhgcnc7y"><p className="text-xs font-semibold text-ink-500" data-id="element-1672" data-mp="c67issblkz8k">الرسوم</p><p className="mt-2 font-display text-3xl text-ink-900" data-id="element-1673" data-mp="c4o1qzi4ak5q">{track.price} <span className="font-sans text-sm text-ink-500" data-id="element-1674" data-mp="gy8zqrjpne4x">/ اعتماد</span></p><div className="mt-5 space-y-3" data-id="element-1675" data-mp="kzkiqe82x145">{track.priceFeatures.map(feature => {
                  const PriceIcon = feature.icon;
                  return <div key={feature.text} className="flex items-start gap-2 text-sm leading-loose text-ink-700" data-id="element-1676" data-mp="pxcj7wmshvjy"><PriceIcon size={14} className="mt-1 shrink-0 text-forest-700" aria-hidden="true" data-id="element-1677" data-mp="uwyx88afzftt" />{feature.text}</div>;
                })}</div><Link to="/ar/waitlist" className="mt-6 block" data-id="element-1678" data-mp="zva8mngdxye5"><Button variant="primary" className="w-full justify-center" data-id="element-1679" data-mp="ztz42lf48mlz">احجز مقعدك</Button></Link></div></div></div></article></Container></section>;
}
function InfoGroup({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="mt-10" data-id="element-1680" data-mp="1it3jqpfeit77"><h4 className="mb-4 text-sm font-semibold text-forest-700" data-id="element-1681" data-mp="1itdesrfazqjr">{title}</h4>{children}</div>;
}
function StandardCard({
  standard,
  index
}: {
  standard: (typeof standards)[number];
  index: number;
}) {
  const Icon = standard.icon;
  const tones = ['bg-sage-100', 'bg-[#EDF2F7]', 'bg-[#F7EDE9]'];
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`rounded-2xl border border-ink-900/5 p-6 shadow-soft ${tones[index]}`} data-id="element-1682" data-mp="15289b8p9841i"><span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-1683" data-mp="1sqrhlo8b0gtl"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-1684" data-mp="1nrx1wv5mv8dp" /></span><h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-1685" data-mp="1nrexhb8thnup">{standard.title}</h3><p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-1686" data-mp="hcw4el0ymi50">{standard.body}</p></motion.article>;
}